import React, { useEffect, useState, useMemo } from "react";
import { Card, Row, Col } from "react-bootstrap";
import { PencilSquare, SignIntersection } from "react-bootstrap-icons";
import EditVitalSignModal from "./EditVitalSignModal";
import { useDispatch, useSelector } from "react-redux";
import { processSupportingInfo } from "features/preauthorization/utils";
import { handleFillStepData } from "../config/claimSlice";

// Import vital sign icons
import TemperatureIcon from "../../../assets/img/icons/temp.svg";
import BloodPressureIcon from "../../../assets/img/blood-pressure.svg";
import PulseIcon from "../../../assets/img/pulse.svg";
import RespiratoryIcon from "../../../assets/img/respiratory-system.svg";
import OxygenIcon from "../../../assets/img/oxygen-saturation.svg";
import WeightIcon from "../../../assets/img/sport.png";
import AdmissionWeight from "../../../assets/img/weight-loss.png";
import BirthWeight from "../../../assets/img/bathroom-scale.png";
import Height from "../../../assets/img/height.svg";

// VitalSignItem component
const VitalSignItem = ({ title, value, icon, unit, bg }) => {
  return (
    <div className="border rounded">
      <div className="d-flex align-items-center">
        <div
          className={`rounded p-2 me-2 d-flex align-items-center justify-content-center ${bg}`}
          style={{ width: 40, height: 40 }}
        >
          <img src={icon} alt={title} width={24} height={24} />
        </div>
        <div>
          <div className="fw-bold text-800 fs-12">{title}</div>
          <div className="d-flex align-items-baseline">
            <span className="fw-normal text-600 fs-12 me-1">{value || ""}</span>
            {unit && value && (
              <span className="text-600 fw-bold fs-12 small">{unit}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ClaimVitalSignRequest = () => {
  const dispatch = useDispatch();
  const [showVitalModal, setShowVitalModal] = useState(false);
  const { claimRequestData, stepFormData, viewClaimInfo } = useSelector(
    (state) => state.claim,
  );
  const { allPermissionType } = useSelector((state) => state.role);
  const claimsSetting = allPermissionType?.[0]?.rolemngmt_name?.find(
    (item) =>
      item?.rolemngmttyp_name === "Claim" ||
      item?.rolemngmttyp_name === "Claims",
  );
  const claimRequest = claimRequestData?.entry?.find(
    (item) => item?.resource?.resourceType === "Claim",
  );

  const payloadClaim = viewClaimInfo?.payload_json;

  // Process supporting info to extract vital sign values
  const vitalDetails = useMemo(
    () => processSupportingInfo(claimRequest?.resource?.supportingInfo || []),
    [claimRequest?.resource?.supportingInfo],
  );

  // Extract vital signs from payloadClaim.sup_info
  const supInfo = useMemo(
    () => payloadClaim?.sup_info || {},
    [payloadClaim?.sup_info],
  );

  const status = viewClaimInfo?.claim_status;

  const isDisabled = [
    "invalid",
    "ready to submit",
    "rejected",
    "cancelled",
    "in process",
    "validation error",
    "invalid",
  ].includes(status?.toLowerCase());

  useEffect(() => {
    // Temperature
    const temp =
      vitalDetails["temperature"]?.valueQuantity?.value ||
      supInfo["5"]?.temp ||
      "";
    if (stepFormData?.["temperature"] !== temp) {
      dispatch(handleFillStepData({ check: "temperature", value: temp }));
    }

    // Systolic Blood Pressure
    const systolic =
      vitalDetails["vital-sign-systolic"]?.valueQuantity?.value ||
      supInfo["1"]?.systlc ||
      "";
    if (stepFormData?.["systolic_blood_pressure"] !== systolic) {
      dispatch(
        handleFillStepData({
          check: "systolic_blood_pressure",
          value: systolic,
        }),
      );
    }

    // Diastolic Blood Pressure
    const diastolic =
      vitalDetails["vital-sign-diastolic"]?.valueQuantity?.value ||
      supInfo["2"]?.diastlc ||
      "";
    if (stepFormData?.["diastolic_blood_pressure"] !== diastolic) {
      dispatch(
        handleFillStepData({
          check: "diastolic_blood_pressure",
          value: diastolic,
        }),
      );
    }

    // Pulse
    const pulse =
      vitalDetails["pulse"]?.valueQuantity?.value || supInfo["8"]?.plse || "";
    if (stepFormData?.["pulse"] !== pulse) {
      dispatch(handleFillStepData({ check: "pulse", value: pulse }));
    }

    // Respiratory Rate
    const respRate =
      vitalDetails["respiratory-rate"]?.valueQuantity?.value ||
      supInfo["12"]?.resprte ||
      "";
    if (stepFormData?.["respiratory_rate"] !== respRate) {
      dispatch(
        handleFillStepData({ check: "respiratory_rate", value: respRate }),
      );
    }

    // Oxygen Saturation
    const oxygen =
      vitalDetails["oxygen-saturation"]?.valueQuantity?.value ||
      supInfo["10"]?.oxystrn ||
      "";
    if (stepFormData?.["oxygen_saturation"] !== oxygen) {
      dispatch(
        handleFillStepData({ check: "oxygen_saturation", value: oxygen }),
      );
    }

    // Weight
    const weight =
      vitalDetails["weight"]?.valueQuantity?.value || supInfo["3"]?.wgt || "";
    if (stepFormData?.["weight"] !== weight) {
      dispatch(handleFillStepData({ check: "weight", value: weight }));
    }

    // Height
    const height =
      vitalDetails["height"]?.valueQuantity?.value || supInfo["4"]?.hgt || "";
    if (stepFormData?.["height"] !== height) {
      dispatch(handleFillStepData({ check: "height", value: height }));
    }

    // Admission Weight
    const admWeight =
      vitalDetails["admission-weight"]?.valueQuantity?.value ||
      supInfo["24"]?.admnwgt ||
      "";
    if (stepFormData?.["admission_weight"] !== admWeight) {
      dispatch(
        handleFillStepData({ check: "admission_weight", value: admWeight }),
      );
    }
  }, []);

  // Define vital signs with dynamic values from stepFormData
  const vitalSigns = [
    {
      title: "Temperature",
      value: stepFormData?.temperature || "",
      unit: vitalDetails["temperature"]?.valueQuantity?.unit || "°C",
      icon: TemperatureIcon,
      bg: "badge-subtle-primary",
    },
    {
      title: "SBP / DBP",
      value: `${stepFormData?.systolic_blood_pressure || ""}/${
        stepFormData?.diastolic_blood_pressure || ""
      }`,
      unit: vitalDetails["vital-sign-systolic"]?.valueQuantity?.unit || "mmHg",
      icon: BloodPressureIcon,
      bg: "badge-subtle-danger",
    },
    {
      title: "Pulse Rate",
      value: stepFormData?.pulse || "",
      unit: vitalDetails["pulse"]?.valueQuantity?.unit || "bpm",
      icon: PulseIcon,
      bg: "badge-subtle-secondary",
    },
    {
      title: "Respiratory",
      value: stepFormData?.respiratory_rate || "",
      unit: vitalDetails["respiratory-rate"]?.valueQuantity?.unit || "rpm",
      icon: RespiratoryIcon,
      bg: "badge-subtle-success",
    },
    {
      title: "O2 Saturation",
      value: stepFormData?.oxygen_saturation || "",
      unit: vitalDetails["oxygen-saturation"]?.valueQuantity?.unit || "%",
      icon: OxygenIcon,
      bg: "badge-subtle-warning",
    },
    {
      title: "Weight",
      value: stepFormData?.weight || "",
      unit: vitalDetails["weight"]?.valueQuantity?.unit || "kg",
      icon: WeightIcon,
      bg: "badge-subtle-success",
    },
    {
      title: "Admission Weight",
      value: stepFormData?.admission_weight || "",
      unit: vitalDetails["admission-weight"]?.valueQuantity?.unit || "kg",
      icon: AdmissionWeight,
      bg: "badge-subtle-info",
    },
    {
      title: "Birth Weight",
      value: stepFormData?.birth_weight || "",
      unit: vitalDetails["birth-weight"]?.valueQuantity?.unit || "kg",
      icon: BirthWeight,
      bg: "badge-subtle-secondary",
    },
    {
      title: "Height",
      value: stepFormData?.height || "",
      unit: vitalDetails["vital-sign-height"]?.valueQuantity?.unit || "kg",
      icon: Height,
      bg: "badge-subtle-primary",
    },
  ];

  return (
    <>
      <div>
        <Card.Header className="d-flex badge-subtle-info justify-content-between border-info align-items-center border-bottom">
          <strong className="fs-12 fw-bold text-info">
            <SignIntersection className="me-2" /> Vital Sign Information
          </strong>
          {isDisabled && Number(claimsSetting?.submit) === 1 && (
            <PencilSquare
              size={12}
              onClick={() => setShowVitalModal(true)}
              className="text-info"
              role="button"
            />
          )}
        </Card.Header>
        <Card.Body className="p-2 mb-0">
          <Row>
            {vitalSigns.map((vital, index) => (
              <Col key={index} xs={12} sm={6} md={4} className="mb-2">
                <VitalSignItem
                  title={vital.title}
                  value={vital.value}
                  unit={vital.unit}
                  icon={vital.icon}
                  bg={vital.bg}
                />
              </Col>
            ))}
          </Row>
        </Card.Body>
      </div>
      <EditVitalSignModal
        show={showVitalModal}
        onHide={() => setShowVitalModal(false)}
      />
    </>
  );
};

export default ClaimVitalSignRequest;
